import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Agent, BrokerageFirm } from "types";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";

import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { sendTriggerEmails } from "utils/emailService";
import { fetchUserData } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { postUserLicenseToggle } from "../../utils/userLicenseToggle";

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Brokerage Firm Affiliated": string;
  "Office": string;
  "Address": string;
  "License Status": string;
  "Status": string;
}

// Types for brokerage firms
type BrokerageOption = { 
  value: string; 
  label: string; 
  raw: any // Store the original API response item
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Agent>();

const SignageAgents = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filtered, setFiltered] = useState<Agent[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.agents
  );

  const [toastShown, setToastShown] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  // const indexOfLastItem = currentPage * itemsPerPage;
  // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentAgents = filteredAgents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAgents?.length / itemsPerPage);
  const [brokerageFirms, setBrokerageFirms] = useState<BrokerageFirm[]>([]);
  const [brokerageFirmFilter, setBrokerageFirmFilter] = useState("");
  const [selectedBrokerage, setSelectedBrokerage] = useState<BrokerageOption | null>(null);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const topRef = useRef<HTMLDivElement>(null);
  const hasShownToast = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");

  // State for parent signage firm (for signageteammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  // Select2 API configuration
  const PAGE_SIZE = 10;

  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        
        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };
  
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        // Build URL with pagination and search
        const params = new URLSearchParams({ 
          licenseType: "BROKERAGE_FIRM",
          signageFirmID: (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "",
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        const search = (inputValue || "").trim();
        if (search) params.set("search", search);

        const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;
        console.log('Loading brokerage options:', { page, search, url });
        
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const responseData = await response.json();
        console.log('Brokerage API response:', { page, search, responseData });

        // Handle Select2-like API response format
        const results = responseData.results || [];
        const hasMore = responseData.pagination?.more || false;

        // Filter only BROKERAGE_FIRM license types and map to react-select options
        const options: BrokerageOption[] = results
          // .filter((item: any) => item.licenseType === "BROKERAGE_FIRM")
          .map((item: any) => ({
            value: item.id || item._id,
            label: item.firmDetails?.companyName || item.text || `${item.firstName} ${item.lastName}`,
            raw: item, // Store the original item
          }));

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        console.error("Error loading brokerage options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId]
  );

  // useEffect(() => {
  //   if (location.state?.successMessage && !hasShownToast.current) {
  //     hasShownToast.current = true;
  //     // console.log(location.state.successMessage,"location.state.successMessage");
  //     setTimeout(() => {
  //       toast.success(location.state.successMessage);  // <-- use dynamic message here
  //       window.history.replaceState({}, document.title); // Clear state so message does not repeat
  //     }, 0);
  //   }
  // }, [location.state?.successMessage]);


  // useEffect(() => {
  //   let filtered = agents;

  //   if (searchQuery) {
  //     filtered = filtered?.filter(agent =>
  //       `${agent?.firstName} ${agent?.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  //     );
  //   }

  //   if (statusFilter) {
  //     filtered = filtered?.filter(agent => agent?.status === statusFilter);
  //   }

  //   setFilteredAgents(filtered);
  //   // setCurrentPage(1); // Reset to first page when filters change
  // }, [agents, searchQuery, statusFilter]);

  useEffect(() => {
    if (isSignageTeammember && !parentSignageId) return;
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
    if (!signageId || !token) return;

    const fetchAgents = async () => {
      const requestId = ++fetchRequestIdRef.current;
      const showInitialLoader = !hasLoadedDataRef.current;
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/agents/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                'agentDetails.brokerageFirmAffiliation': brokerageFirmFilter,
                // status: "Active",
                // LicenseActive: "true",
              },
              sort: {
                field: "updated_at",
                order: "desc",
              },
              fields: [
                "firstName",
                "lastName",
                "email",
                "phone1",
                "status",
                "LicenseActive",
                "firmDetails.companyName",
                "agentDetails",
                "firmDetails.address",
                "offices",
                "created_at",
              ],
            }),
          }
        );

        if (!response.ok) {
          showError("Access denied. You don’t have view permission for Agents.");
        }

        const result = await response.json();
        if (requestId !== fetchRequestIdRef.current) return;
        setRowCount(result.total);

        const agentData = result?.data ?? [];
        if (Array.isArray(agentData)) {
          setAgents(agentData);
          setFilteredAgents(agentData);
          hasLoadedDataRef.current = true;
        } else {
          throw new Error("Unexpected response format for agents");
        }

      } catch (err) {
        if (requestId !== fetchRequestIdRef.current) return;
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchAgents();
  }, [
    token,
    userId,
    isSignageTeammember,
    parentSignageId,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    deleting,
    brokerageFirmFilter,
  ]);


  // const fetchAllAgents = async () => {
  //   const response = await fetch(
  //     `${process.env.REACT_APP_API_URL}/users/signage/${userId}/agents/datatable`,
  //     {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({
  //         page: 1,
  //         limit: 1000,
  //         search: globalFilter,
  //         filters: {
  //           status: statusFilter || undefined,
  //           "firmDetails.companyName": brokerageFirmFilter || undefined,
  //         },
  //         sort: {
  //               field: "created_at",
  //               order: "desc",
  //             },
  //         fields: [
  //           "id",
  //           "firstName",
  //           "lastName",
  //           "email",
  //           "phone1",
  //           "status",
  //           "LicenseActive",
  //           "firmDetails.companyName",
  //           "agentDetails",
  //           "offices",
  //           "created_at",
  //         ],
  //       }),
  //     }
  //   );

  //   const result = await response.json();
  //   return result.data || [];
  // };

  // useEffect(() => {
  //   const updateFilteredAgents = async () => {
  //     const allAgents = await fetchAllAgents();
  //     setAgents(allAgents);
  //   };

  //   updateFilteredAgents();
  // }, [globalFilter, brokerageFirmFilter]);

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);

  // Handler for brokerage selection change
  const handleBrokerageChange = (opt: BrokerageOption | null) => {
    setSelectedBrokerage(opt);
    setBrokerageFirmFilter(opt ? opt.label : "");
  };

  // Search and filter logic
  // useEffect(() => {
  //   let filteredData = [...agents];
  //   if (searchQuery) {
  //     filteredData = filteredData?.filter((m) =>
  //       `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  //     );
  //   }
  //   if (brokerageFirmFilter) {
  //     filteredData = filteredData?.filter((m) => m.agentDetails.brokerageFirmAffiliation === brokerageFirmFilter);
  //   }
  //   // if (statusFilter) {
  //   //   filteredData = filteredData.filter((m) => m.status === statusFilter);
  //   // }
  //   setFiltered(filteredData);
  // }, [searchQuery, brokerageFirmFilter, agents]);

  const confirmDeleteAgent = async () => {
    if (!deleteTargetId) return;
    setDeleting(deleteTargetId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${deleteTargetId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      showSuccess("Agent deleted successfully!");
      setAgents((prev) =>
        prev?.filter((agent) => agent.id !== deleteTargetId)
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

  const handleDeleteClick = (agent: Agent) => {
    const fullName = [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim();
    setDeleteTargetId(agent.id);
    setDeleteTargetName(fullName || "Agent");
    setOpenDelete(true);
  };

  const handleActivate = async (agentId: string) => {
    setDeleting(agentId); // Set the license being deleted

    try {
      const api = await postUserLicenseToggle(token, agentId, "activate");
      if (!api.ok) {
        console.error("API Error:", api.message);
        showError(api.message);
        throw new Error(api.message);
      }

      showSuccess(api.message);
      // UPDATE agent status locally
      const updatedAgents = agents.map(agent =>
          agent.id === agentId
            ? { ...agent, LicenseActive: true }
            : agent
      );
      setAgents(updatedAgents);
      setFilteredAgents(updatedAgents);
      
      // Fetch agent data using agentId
      const activatedAgent = await fetchUserData(agentId, token);

      // ✅ Trigger: Agent License Activated - Send notifications
      if (activatedAgent) {
        try {
          const { createNotification } = await import("../../services/notificationApi");
          const { getAgentLicenseActivatedContent } = await import("../../services/notificationContent");
          
          const agentName = `${activatedAgent?.firstName || ""} ${activatedAgent?.lastName || ""}`.trim() || "Agent";
             const brokerageFirmID = (activatedAgent as any).agentDetails?.brokerageFirmID;
          // Send notification to Agent
          if (agentId && token) {
            const agentContent = getAgentLicenseActivatedContent({
              agentName,
              recipientType: "agent",
            });
            await createNotification(agentId, agentContent, token, "agent");
          }

          // Send notification to Signage Admin/Team Member
          if (userId && token) {
            const signageContent = getAgentLicenseActivatedContent({
              agentName,
              recipientType: "signage_team",
            });
            // Map signage_team to actual recipient type based on user's license
            const signageRecipientType = activatedAgent?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin";
            await createNotification(userId, signageContent, token, signageRecipientType);
          }
           if (brokerageFirmID && token) {
            const brokerageContent = getAgentLicenseActivatedContent({
              agentName,
              recipientType: "brokerage_team",
            });
            // Map brokerage_team - need to fetch brokerage user data to determine type
            try {
              const brokerageUserData = await fetchUserData(brokerageFirmID, token);
              const brokerageRecipientType = brokerageUserData?.licenseType === "BROKERAGE_TEAM_MEMBER" ? "brokerage_team_member" : "brokerage_admin";
              await createNotification(brokerageFirmID, brokerageContent, token, brokerageRecipientType);
            } catch (err) {
              // Fallback to brokerage_admin if fetch fails
              await createNotification(brokerageFirmID, brokerageContent, token, "brokerage_admin");
            }
          }
        } catch (notifError) {
          console.warn("Failed to send agent license activated notification:", notifError);
          // Don't block success if notification fails
        }
      }
      
      // Send activation emails based on brokerageFirmID
      if (activatedAgent) {
        try {
          const from = process.env.REACT_APP_FROM_EMAIL2;
          if (from && activatedAgent.email) {
            // Fetch signage admin data
            const adminData = await fetchUserData(userId, token);
            const adminEmail = adminData?.email || "";
            const adminFirstName = adminData?.firstName || adminData?.email?.split("@")[0] || "Signage Admin";
            
            const agentName = `${activatedAgent.firstName || ""} ${activatedAgent.lastName || ""}`.trim() || "Agent";
            const activatedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
            const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
            const loginUrl = `${baseUrl}/login`;
            
            // Get agent details from the fetched data
            const officeName = (activatedAgent as any).offices?.[0]?.officeName || "";
            const brokerageFirmID = (activatedAgent as any).agentDetails?.brokerageFirmID;
            const brokerageName = (activatedAgent as any).agentDetails?.brokerageFirmName || "";
            
            // Check if brokerageFirmID exists
            if (brokerageFirmID) {
              // Fetch brokerage firm admin data using userDataFetcher
              let brokerageAdminData = null;
              
              try {
                brokerageAdminData = await fetchUserData(brokerageFirmID, token);
              } catch (brokerageError) {
                console.warn("Failed to fetch brokerage admin:", brokerageError);
              }
              
              // Prepare email params
              const emailParams = {
                agentName: agentName,
                firstName: activatedAgent.firstName || "",
                adminFirstName: adminFirstName,
                agentEmail: activatedAgent.email,
                activatedOn: activatedOn,
                officeName: officeName,
                brokerageName: brokerageName,
                loginUrl: loginUrl,
              };
              
              // Send separate emails to each recipient with their specific license type
              // 1. Send to Signage Admin/Team Member
              if (adminEmail && adminData) {
                const adminLicenseType = adminData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "SIGNAGE_TEAM_MEMBER" : "SIGNAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license activated",
                  adminLicenseType,
                  emailParams,
                  adminEmail,
                  from,
                  token || ""
                );
              }
              
              // 2. Send to Brokerage Admin/Team Member
              if (brokerageAdminData?.email) {
                const brokerageLicenseType = brokerageAdminData.licenseType === "BROKERAGE_TEAM_MEMBER" ? "BROKERAGE_TEAM_MEMBER" : "BROKERAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license activated",
                  brokerageLicenseType,
                  emailParams,
                  brokerageAdminData.email,
                  from,
                  token || ""
                );
              }
              
              // 3. Send to BROKERAGE_AGENT
              await sendTriggerEmails(
                "Signage Agent license activated",
                "BROKERAGE_AGENT",
                emailParams,
                activatedAgent.email,
                from,
                token || ""
              );
            } else {
              // No brokerageFirmID - send to Signage Admin/Team Member and AGENT
              const emailParams = {
                agentName: agentName,
                firstName: activatedAgent.firstName || "",
                adminFirstName: adminFirstName,
                agentEmail: activatedAgent.email,
                activatedOn: activatedOn,
                loginUrl: loginUrl,
              };
              
              // Send separate emails to each recipient with their specific license type
              // 1. Send to Signage Admin/Team Member
              if (adminEmail && adminData) {
                const adminLicenseType = adminData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "SIGNAGE_TEAM_MEMBER" : "SIGNAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license activated",
                  adminLicenseType,
                  emailParams,
                  adminEmail,
                  from,
                  token || ""
                );
              }
              
              // 2. Send to AGENT
              await sendTriggerEmails(
                "Signage Agent license activated",
                "AGENT",
                emailParams,
                activatedAgent.email,
                from,
                token || ""
              );
            }
          }
        } catch (emailError) {
          console.warn("Failed to send activation emails:", emailError);
          // Don't fail the activation if email fails
        }
      }
      
      // navigate(0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while activating."
      );

    } finally {
      setDeleting(null);
    }
  };

  const handleDeactivate = async (agentId: string) => {
    setDeleting(agentId); // Set the license being deleted

    try {
      const api = await postUserLicenseToggle(token, agentId, "deactivate");
      if (!api.ok) {
        console.error("API Error:", api.message);
        showError(api.message);
        throw new Error(api.message);
      }
      showSuccess(api.message);
      // UPDATE agent status locally
      const updatedAgents = agents.map(agent =>
          agent.id === agentId
            ? { ...agent, LicenseActive: false }
            : agent
      );
      setAgents(updatedAgents);
      setFilteredAgents(updatedAgents);
      
      // Fetch agent data using agentId
      const deactivatedAgent = await fetchUserData(agentId, token);

      // ✅ Trigger: Agent License Deactivated - Send notifications
      if (deactivatedAgent) {
        try {
          const { createNotification } = await import("../../services/notificationApi");
          const { getAgentLicenseDeactivatedContent } = await import("../../services/notificationContent");
          
          const agentName = `${deactivatedAgent?.firstName || ""} ${deactivatedAgent?.lastName || ""}`.trim() || "Agent";
             const brokerageFirmID = (deactivatedAgent as any).agentDetails?.brokerageFirmID;
          // Send notification to Agent
          if (agentId && token) {
            const agentContent = getAgentLicenseDeactivatedContent({
              agentName,
              recipientType: "agent",
            });
            await createNotification(agentId, agentContent, token, "agent");
          }

          // Send notification to Signage Admin/Team Member
          if (userId && token) {
            const signageContent = getAgentLicenseDeactivatedContent({
              agentName,
              recipientType: "signage_team",
            });
            // Map signage_team to actual recipient type based on user's license
            const signageRecipientType = deactivatedAgent?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin";
            await createNotification(userId, signageContent, token, signageRecipientType);
          }
            if (brokerageFirmID && token) {
            const brokerageContent = getAgentLicenseDeactivatedContent({
              agentName,
              recipientType: "brokerage_team",
            });
            // Map brokerage_team - need to fetch brokerage user data to determine type
            try {
              const brokerageUserData = await fetchUserData(brokerageFirmID, token);
              const brokerageRecipientType = brokerageUserData?.licenseType === "BROKERAGE_TEAM_MEMBER" ? "brokerage_team_member" : "brokerage_admin";
              await createNotification(brokerageFirmID, brokerageContent, token, brokerageRecipientType);
            } catch (err) {
              // Fallback to brokerage_admin if fetch fails
              await createNotification(brokerageFirmID, brokerageContent, token, "brokerage_admin");
            }
          }
        } catch (notifError) {
          console.warn("Failed to send agent license deactivated notification:", notifError);
          // Don't block success if notification fails
        }
      }
      
      // Send deactivation emails based on brokerageFirmID
      if (deactivatedAgent) {
        try {
          const from = process.env.REACT_APP_FROM_EMAIL2;
          if (from && deactivatedAgent.email) {
            // Fetch signage admin data
            const adminData = await fetchUserData(userId, token);
            const adminEmail = adminData?.email || "";
            const adminFirstName = adminData?.firstName || adminData?.email?.split("@")[0] || "Signage Admin";
            
            const agentName = `${deactivatedAgent.firstName || ""} ${deactivatedAgent.lastName || ""}`.trim() || "Agent";
            const deactivatedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
            
            // Get agent details from the fetched data
            const brokerageFirmID = (deactivatedAgent as any).agentDetails?.brokerageFirmID;
            const brokerageName = (deactivatedAgent as any).agentDetails?.brokerageFirmName || "";
            
            // Check if brokerageFirmID exists
            if (brokerageFirmID) {
              // Fetch brokerage firm admin data using userDataFetcher
              let brokerageAdminData = null;
              
              try {
                brokerageAdminData = await fetchUserData(brokerageFirmID, token);
              } catch (brokerageError) {
                console.warn("Failed to fetch brokerage admin:", brokerageError);
              }
              
              // Prepare email params
              const emailParams = {
                agentName: agentName,
                firstName: deactivatedAgent.firstName || "",
                adminFirstName: adminFirstName,
                agentEmail: deactivatedAgent.email,
                deactivatedOn: deactivatedOn,
                brokerageName: brokerageName,
              };
              
              // Send separate emails to each recipient with their specific license type
              // 1. Send to Signage Admin/Team Member
              if (adminEmail && adminData) {
                const adminLicenseType = adminData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "SIGNAGE_TEAM_MEMBER" : "SIGNAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license deactivated",
                  adminLicenseType,
                  emailParams,
                  adminEmail,
                  from,
                  token || ""
                );
              }
              
              // 2. Send to Brokerage Admin/Team Member
              if (brokerageAdminData?.email) {
                const brokerageLicenseType = brokerageAdminData.licenseType === "BROKERAGE_TEAM_MEMBER" ? "BROKERAGE_TEAM_MEMBER" : "BROKERAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license deactivated",
                  brokerageLicenseType,
                  emailParams,
                  brokerageAdminData.email,
                  from,
                  token || ""
                );
              }
              
              // 3. Send to BROKERAGE_AGENT
              await sendTriggerEmails(
                "Signage Agent license deactivated",
                "BROKERAGE_AGENT",
                emailParams,
                deactivatedAgent.email,
                from,
                token || ""
              );
            } else {
              // No brokerageFirmID - send to Signage Admin/Team Member and AGENT
              const emailParams = {
                agentName: agentName,
                firstName: deactivatedAgent.firstName || "",
                adminFirstName: adminFirstName,
                agentEmail: deactivatedAgent.email,
                deactivatedOn: deactivatedOn,
              };
              
              // Send separate emails to each recipient with their specific license type
              // 1. Send to Signage Admin/Team Member
              if (adminEmail && adminData) {
                const adminLicenseType = adminData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "SIGNAGE_TEAM_MEMBER" : "SIGNAGE_FIRM";
                await sendTriggerEmails(
                  "Signage Agent license deactivated",
                  adminLicenseType,
                  emailParams,
                  adminEmail,
                  from,
                  token || ""
                );
              }
              
              // 2. Send to AGENT
              await sendTriggerEmails(
                "Signage Agent license deactivated",
                "AGENT",
                emailParams,
                deactivatedAgent.email,
                from,
                token || ""
              );
            }
          }
        } catch (emailError) {
          console.warn("Failed to send deactivation emails:", emailError);
          // Don't fail the deactivation if email fails
        }
      }
      
      // navigate(0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deactivating."
      );

    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    console.log("filteredAgents", filteredAgents);
  }, [filteredAgents]);

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US"); // Outputs in MM/DD/YYYY format
  }
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor((row: Agent) => row?.agentDetails?.brokerageFirmAffiliation ?? "-", {
        id: "brokerageFirmAffiliation",
        header: () => <div className="th-text">Brokerage Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: Agent) => row.firstName, {
        id: "firstName",
        header: () => <div className="th-text">First Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: Agent) => row.lastName, {
        id: "lastName",
        header: () => <div className="th-text">Last Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: Agent) => row?.offices?.[0]?.officeName ?? "-", {
        id: "officeName",
        header: () => <div className="th-text">Office Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: Agent) => row?.email ?? "-", {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: Agent) => row?.phone1 ?? "-", {
        id: "phone",
        header: () => <div className="th-text">Phone</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      // columnHelper.display({
      //   id: "licensestatus",
      //   header: () => <div className="th-text">License Status</div>,
      //   cell: (info) => {
      //     const agentInfo = info.row.original;
      //     const isActive = agentInfo.LicenseActive;
      //     const buttonClass = isActive
      //       ? "license-btn license-deactivate"
      //       : "license-btn license-activate";

      //     return (
      //       <div className="text-sm">
      //         <button
      //           className={buttonClass}
      //           onClick={() =>
      //             isActive ? handleDeactivate(agentInfo.id) : handleActivate(agentInfo.id)
      //           }
      //         >
      //           {isActive ? "Deactivate" : "Activate"}
      //         </button>
      //       </div>
      //     );
      //   },
      // }),

      columnHelper.accessor("status", {
        header: () => <div className="th-text">Agent Status</div>,
        cell: info => {
          const value = info.getValue();
          const isActive = value === "Active";

          // pick one of two marker-colors
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="">
              <input
                type="radio"
                className={className}
                checked={true}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text ">Actions</div>,
        cell: (info) => {
          const agent = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/view-agent/${agent.id}`} className="btn action-btn " title="View" />
              )}

              {canEdit && (
              <EditIconButton
                to={`${baseRoute}/agent/${agent.id}`} state={{ type: "edit" }} className="icon-square" title="Edit" />
              )}

              {canDelete && (
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => handleDeleteClick(agent)}
                disabled={deleting === agent.id}
              />
              )}
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, canView, canEdit, canDelete]);


  useEffect(() => {
    const rows: ExportRow[] = filteredAgents.map(agent => {
      // Format address with all address components
      const addressComponents = [
        agent.firmDetails?.address?.addressLine1,
        agent.firmDetails?.address?.addressLine2,
        agent.firmDetails?.address?.city,
        agent.firmDetails?.address?.state,
        agent.firmDetails?.address?.zip,
        agent.firmDetails?.address?.country,
      ].filter(Boolean); // Remove empty/undefined values
      
      const formattedAddress = addressComponents.join(', ');

      return {
        "First Name": agent.firstName || '',
        "Last Name": agent.lastName || '',
        "Email": agent.email || '',
        "Phone": agent.phone1 || '',
        "Brokerage Firm Affiliated": agent.agentDetails?.brokerageFirmAffiliation || '',
        "Office": agent.offices?.[0]?.officeName || '',
        "Address": formattedAddress,
        "License Status": agent?.LicenseActive ? 'Active' : 'Inactive',
        "Status": agent.status === 'Active' ? 'Active' : 'Inactive',
      };
    });
    setExportRows(rows);
  }, [filteredAgents]);


  const table = useReactTable({
    data: agents,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  const showTableLoader = loading;

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  return (
    <>
    <div className="content" ref={topRef}>
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/addAgent`} state={{ type: "add" }} className="btn btn-primary table-addbutton">
            + Add Agent
          </Link>
          )}

        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Agents</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search — form wrapper helps browsers honor autocomplete=off */}
            <form
              className="input-group po-search"
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                setGlobalFilter(searchInput);
              }}
            >
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="signageAgentsSearchInput"
                name="signage_agents_datatable_search"
                className="form-control"
                placeholder="Search by company Name, Email"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </form>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <ExportAsExcel
              data={exportRows}
              headers={[
                "First Name",
                "Last Name",
                "Email",
                "Phone",
                "Brokerage Firm Affiliated",
                "Office",
                "Address",
                "License Status",
                "Status",
              ]}
              fileName="Agents"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>

        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-md-4">
                <div className="po-title-wrap me-2 d-flex align-items-center justify-content-end">
                  <AsyncPaginate
                    classNamePrefix="rs"
                    placeholder="Search brokerage firms..."
                    value={selectedBrokerage}
                    loadOptions={loadBrokerageOptions}
                    onChange={handleBrokerageChange}
                    additional={{ page: 1 }}
                    debounceTimeout={300}
                    isClearable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '38px',
                        border: state.isFocused ? '1px solid #86b7fe' : '1px solid #ced4da',
                        borderRadius: '0.375rem',
                        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                        '&:hover': {
                          borderColor: '#86b7fe'
                        }
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#6c757d',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        border: '1px solid #dee2e6',
                        borderRadius: '0.375rem',
                        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : 'white',
                        color: state.isSelected ? 'white' : '#212529',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#0d6efd' : '#e7f1ff'
                        }
                      })
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading agents…" variant="dots" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading agents…" variant="dots" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="firstName"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
    </div>

    <DeleteConfirmModal
      open={openDelete}
      dynamicName={deleteTargetName || "Agent"}
      onCancel={() => {
        setOpenDelete(false);
        setDeleteTargetId(null);
        setDeleteTargetName("");
      }}
      onDelete={confirmDeleteAgent}
    />
    </>

  );
};

export default SignageAgents;
